/**
 * Bonds between two guests — what they are, and what the seater must do.
 *
 * A tag is a set membership; a bond is an edge, and one cannot do the other's
 * job. The reason is short and worth keeping in front of anyone editing this:
 * **there is no tag that means "apart"**. Negative constraints are irreducibly
 * pairwise, so a tag-only model silently drops the single most expensive thing
 * a host knows about a room.
 *
 * `relation` (what they are) and `seating` (what to do) are separate on purpose.
 * "They're cousins, keep them apart" is one honest fact, and a single kind
 * column would force it into two rows that disagree.
 *
 * The server owns the canonical pair ordering — one edge between two people,
 * never one per direction — so nothing in the browser computes it. What the
 * browser does own is reading a link from ONE guest's point of view, which is
 * what {@link linksForGuest} is: the same row means "her partner is Dev" on her
 * record and "his partner is Anna" on his.
 */
import type { GuestLink, TrackerGuest } from '../services/tracker-api-client';

export const LINK_RELATIONS = [
  'partner',
  'family',
  'household',
  'friend',
  'colleague',
  'ex',
  'other',
] as const;
export type LinkRelation = (typeof LINK_RELATIONS)[number];

export const LINK_RELATION_LABELS: Readonly<Record<LinkRelation, string>> = {
  partner: 'Partner',
  family: 'Family',
  household: 'Same household',
  friend: 'Friend',
  colleague: 'Colleague',
  ex: 'Ex',
  other: 'Other',
};

export const LINK_SEATINGS = ['together', 'apart', 'any'] as const;
export type LinkSeating = (typeof LINK_SEATINGS)[number];

export const LINK_SEATING_LABELS: Readonly<Record<LinkSeating, string>> = {
  together: 'Seat together',
  apart: 'Keep apart',
  any: 'No seating rule',
};

/*
  There is no strength vocabulary here.

  `strength` is a real column and `describeLink` reads it — the API can send
  'prefer' and the record says so — but nothing in Studio offers the choice:
  `createGuestLink` sends relation and seating only, and the server defaults the
  rest to 'must'. A must/prefer picker is a decision about what a host is asked
  for at the moment they are recording "keep these two apart", not a constant to
  leave lying about; the labels sat here unused, advertising a control that does
  not exist.
*/

/**
 * The default seating for a relation, offered — never forced.
 *
 * Partners and households nearly always want to sit together and an "ex" almost
 * never does, so pre-selecting saves the second dropdown on the common case.
 * The host can change it, which is the whole reason the two are separate
 * columns: "cousins, keep them apart" has to remain expressible.
 */
export function defaultSeatingFor(relation: string): LinkSeating {
  if (relation === 'partner' || relation === 'household') return 'together';
  if (relation === 'ex') return 'apart';
  return 'any';
}

export interface GuestLinkView {
  link: GuestLink;
  /** The OTHER person, from the point of view of the guest being read. */
  otherInviteeId: string;
  otherName: string;
  /** "Partner · seat together" — what the record shows on one line. */
  summary: string;
}

/**
 * One guest's bonds, each read from their side.
 *
 * `otherId` is whichever end of the row is not this guest, so a record never
 * shows a guest linked to themself and never has to know which id the server
 * sorted first.
 */
export function linksForGuest(
  links: ReadonlyArray<GuestLink>,
  inviteeId: string,
  roster: ReadonlyArray<Pick<TrackerGuest, 'inviteeId' | 'fullName'>>,
): GuestLinkView[] {
  const names = new Map(roster.map((guest) => [guest.inviteeId, guest.fullName]));
  return links
    .filter((link) => link.inviteeId === inviteeId || link.otherId === inviteeId)
    .map((link) => {
      const otherInviteeId = link.inviteeId === inviteeId ? link.otherId : link.inviteeId;
      // A name we do not hold is a guest the roster read has not caught up on,
      // not a broken link — so it says so rather than rendering a bare uuid.
      const otherName = names.get(otherInviteeId) ?? 'Someone not on the list yet';
      return { link, otherInviteeId, otherName, summary: describeLink(link) };
    })
    .sort((a, b) => a.otherName.localeCompare(b.otherName));
}

/** "Partner · keep apart (prefer)" — the one line a host reads on the record. */
export function describeLink(link: Pick<GuestLink, 'relation' | 'seating' | 'strength'>): string {
  const relation =
    LINK_RELATION_LABELS[link.relation as LinkRelation] ?? LINK_RELATION_LABELS.other;
  const seating = link.seating as LinkSeating;
  if (seating === 'any') return relation;
  const rule = LINK_SEATING_LABELS[seating] ?? LINK_SEATING_LABELS.any;
  // "Prefer" is worth saying out loud; "must" is the default and saying it on
  // every row is noise the host stops reading.
  const hedge = link.strength === 'prefer' ? ' (prefer)' : '';
  return `${relation} · ${rule.toLowerCase()}${hedge}`;
}

/**
 * Who this guest can still be linked to.
 *
 * Themself is excluded, and so is anyone already linked — the API answers 409
 * on a duplicate pair, and a picker that offers a name it cannot add reads as a
 * broken button. Filters the roster the page already holds rather than asking
 * the server: there is no `/invitees/search`, deliberately, because a second
 * matching surface is how the two rules drift apart.
 */
export function linkCandidates(
  roster: ReadonlyArray<Pick<TrackerGuest, 'inviteeId' | 'fullName'>>,
  links: ReadonlyArray<GuestLink>,
  inviteeId: string,
  query: string,
): Array<Pick<TrackerGuest, 'inviteeId' | 'fullName'>> {
  const taken = new Set(
    links
      .filter((link) => link.inviteeId === inviteeId || link.otherId === inviteeId)
      .map((link) => (link.inviteeId === inviteeId ? link.otherId : link.inviteeId)),
  );
  const needle = query.trim().toLowerCase();
  return roster
    .filter((guest) => guest.inviteeId !== inviteeId && !taken.has(guest.inviteeId))
    .filter((guest) => !needle || guest.fullName.toLowerCase().includes(needle))
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
}
